import React, { useState, useMemo } from "react";
import {
  useCommissions,
  useCommissionStats,
  useCommissionExportHistory,
  useBulkExportCommissions,
  useBulkRejectCommissions,
  useBulkMarkCommissionsPaid,
} from "../hooks/useCommissions";
import { useLocalStorage } from "../hooks/useLocalStorage";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import { CommissionPayout, CommissionPayoutStatus } from "../services/api";
import {
  FunnelIcon,
  BanknotesIcon,
  DocumentArrowDownIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  EyeIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

type FilterStatus = "all" | CommissionPayoutStatus;

const Commissions: React.FC = () => {
  const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] =
    useState<CommissionPayout | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isExportHistoryModalOpen, setIsExportHistoryModalOpen] =
    useState(false);
  const [filter, setFilter] = useLocalStorage<FilterStatus>(
    "commissions-filter",
    "all",
  );
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "commissions-search-query",
    "",
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);

  const { data: commissionsResponse, isLoading } = useCommissions({
    limit: itemsPerPage,
    page: currentPage,
    search: searchQuery || undefined,
    status: filter !== "all" ? filter : undefined,
  });

  const { data: stats } = useCommissionStats();
  const { data: exportHistoryData } = useCommissionExportHistory(10);

  const bulkExport = useBulkExportCommissions();
  const bulkReject = useBulkRejectCommissions();
  const bulkMarkPaid = useBulkMarkCommissionsPaid();

  const commissions = useMemo(
    () => commissionsResponse?.data || [],
    [commissionsResponse?.data],
  );
  const totalItems = commissionsResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const exportHistory = exportHistoryData?.data || [];

  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  React.useEffect(() => {
    setSelectedCommissions([]);
  }, [commissionsResponse]);

  const handleSelectAll = () => {
    if (selectedCommissions.length === commissions.length) {
      setSelectedCommissions([]);
    } else {
      setSelectedCommissions(commissions.map((c) => c._id));
    }
  };

  const handleSelectCommission = (id: string) => {
    if (selectedCommissions.includes(id)) {
      setSelectedCommissions(selectedCommissions.filter((cid) => cid !== id));
    } else {
      setSelectedCommissions([...selectedCommissions, id]);
    }
  };

  const handleViewDetails = (commission: CommissionPayout) => {
    setSelectedCommission(commission);
    setIsDetailModalOpen(true);
  };

  const handleExport = () => {
    if (selectedCommissions.length === 0) return;
    bulkExport.mutate(selectedCommissions);
  };

  const handleOpenRejectModal = () => {
    if (selectedCommissions.length === 0) return;
    setRejectReason("");
    setIsRejectModalOpen(true);
  };

  const handleReject = () => {
    if (selectedCommissions.length === 0 || !rejectReason.trim()) return;
    bulkReject.mutate(
      {
        commissionPayoutIds: selectedCommissions,
        rejectionReason: rejectReason,
      },
      { onSuccess: () => setIsRejectModalOpen(false) },
    );
  };

  const handleMarkPaid = () => {
    if (selectedCommissions.length === 0) return;
    bulkMarkPaid.mutate(selectedCommissions);
  };

  const getStatusBadge = (status: CommissionPayoutStatus) => {
    const badges: Record<
      CommissionPayoutStatus,
      { bg: string; icon: React.ReactNode; label: string }
    > = {
      open: {
        bg: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
        icon: <ClockIcon className="h-4 w-4 mr-1" />,
        label: "Open",
      },
      in_progress: {
        bg: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
        icon: <ArrowPathIcon className="h-4 w-4 mr-1" />,
        label: "In Progress",
      },
      complete: {
        bg: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
        icon: <CheckCircleIcon className="h-4 w-4 mr-1" />,
        label: "Complete",
      },
      rejected: {
        bg: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
        icon: <XCircleIcon className="h-4 w-4 mr-1" />,
        label: "Rejected",
      },
    };
    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const dealerName = (c: CommissionPayout) =>
    c.dealerSnapshot?.companyName ||
    `${c.dealerSnapshot?.firstName || ""} ${c.dealerSnapshot?.lastName || ""}`.trim() ||
    c.dealerId;

  // Only OPEN can be exported
  const canExport = useMemo(() => {
    return selectedCommissions.some((id) => {
      const c = commissions.find((x) => x._id === id);
      return c?.status === "open";
    });
  }, [selectedCommissions, commissions]);

  // Only IN_PROGRESS can be marked paid
  const canMarkPaid = useMemo(() => {
    return selectedCommissions.some((id) => {
      const c = commissions.find((x) => x._id === id);
      return c?.status === "in_progress";
    });
  }, [selectedCommissions, commissions]);

  // OPEN or IN_PROGRESS can be rejected
  const canReject = useMemo(() => {
    return selectedCommissions.some((id) => {
      const c = commissions.find((x) => x._id === id);
      return c?.status === "open" || c?.status === "in_progress";
    });
  }, [selectedCommissions, commissions]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Commission Payouts
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage dealer commission payout requests
          </p>
        </div>
        <button
          onClick={() => setIsExportHistoryModalOpen(true)}
          className="mt-2 sm:mt-0 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
          Export History
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">Open</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.open || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              In Progress
            </div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {stats.inProgress || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Complete
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.complete || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Amount
            </div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {formatCurrency(stats.totalAmount || 0)}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search by dealer name, company or ID..."
            />
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterStatus)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="complete">Complete</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedCommissions.length > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              {selectedCommissions.length} commission payout(s) selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleExport}
                disabled={!canExport || bulkExport.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                {bulkExport.isPending ? "Exporting..." : "Export"}
              </button>
              <button
                onClick={handleMarkPaid}
                disabled={!canMarkPaid || bulkMarkPaid.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                {bulkMarkPaid.isPending ? "Processing..." : "Mark as Paid"}
              </button>
              <button
                onClick={handleOpenRejectModal}
                disabled={!canReject || bulkReject.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commissions Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : commissions.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        selectedCommissions.length === commissions.length &&
                        commissions.length > 0
                      }
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Payout ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Dealer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount (Net / VAT / Gross)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Bank Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {commissions.map((commission) => (
                  <tr
                    key={commission._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedCommissions.includes(commission._id)}
                        onChange={() => handleSelectCommission(commission._id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900 dark:text-white">
                        {commission.commissionPayoutId}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {dealerName(commission)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {commission.dealerSnapshot?.email ||
                            commission.dealerId}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(commission.grossAmount || 0)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Net {formatCurrency(commission.netAmount || 0)} · VAT{" "}
                          {commission.vatPercent}% (
                          {formatCurrency(commission.vatAmount || 0)})
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900 dark:text-white">
                          {commission.bankDetails?.accountHolder || "-"}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {commission.bankDetails?.iban?.substring(0, 8)}
                          ...
                          {commission.bankDetails?.iban?.slice(-4)}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(commission.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(commission.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetails(commission)}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <BanknotesIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No commission payouts found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all"
              ? "Try adjusting your search or filters"
              : "No commission payout requests yet"}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedCommission && isDetailModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsDetailModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BanknotesIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Commission Payout Details
                    </h3>
                    <p className="text-sm text-primary-100">
                      {selectedCommission.commissionPayoutId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsDetailModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    {getStatusBadge(selectedCommission.status)}
                  </div>

                  {/* Amount */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Amount
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Net
                        </label>
                        <p className="mt-1 text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(selectedCommission.netAmount || 0)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          VAT ({selectedCommission.vatPercent}%)
                        </label>
                        <p className="mt-1 text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(selectedCommission.vatAmount || 0)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Gross
                        </label>
                        <p className="mt-1 text-lg font-bold text-primary-600 dark:text-primary-400">
                          {formatCurrency(selectedCommission.grossAmount || 0)}
                        </p>
                      </div>
                    </div>
                    {selectedCommission.specialText && (
                      <p className="mt-3 text-sm font-medium text-yellow-700 dark:text-yellow-300">
                        {selectedCommission.specialText}
                      </p>
                    )}
                  </div>

                  {/* Dealer */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Dealer
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Company / Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {dealerName(selectedCommission)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Email
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white break-all">
                          {selectedCommission.dealerSnapshot?.email || "-"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Dealer ID
                        </label>
                        <p className="mt-1 text-sm font-mono text-gray-900 dark:text-white">
                          {selectedCommission.dealerId}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          VAT ID
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedCommission.dealerSnapshot?.vatId || "-"}
                        </p>
                      </div>
                      {selectedCommission.dealerSnapshot?.street && (
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Address
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedCommission.dealerSnapshot.street}{" "}
                            {selectedCommission.dealerSnapshot.housenumber}
                            {selectedCommission.dealerSnapshot
                              .addressAddition &&
                              `, ${selectedCommission.dealerSnapshot.addressAddition}`}
                            <br />
                            {selectedCommission.dealerSnapshot.zipCode}{" "}
                            {selectedCommission.dealerSnapshot.city}
                            {selectedCommission.dealerSnapshot.country &&
                              `, ${selectedCommission.dealerSnapshot.country}`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bank Details */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Bank Details
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Account Holder
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedCommission.bankDetails?.accountHolder || "-"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          IBAN
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                          {selectedCommission.bankDetails?.iban || "-"}
                        </p>
                      </div>
                      {selectedCommission.bankDetails?.bic && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            BIC
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedCommission.bankDetails.bic}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rejection Reason */}
                  {selectedCommission.rejectionReason && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-800 dark:text-red-200">
                          Rejection Reason
                        </span>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {selectedCommission.rejectionReason}
                      </p>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Timestamps
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Created
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedCommission.createdAt)}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Updated
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedCommission.updatedAt)}
                        </p>
                      </div>
                      {selectedCommission.exportedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Exported
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedCommission.exportedAt)}
                          </p>
                        </div>
                      )}
                      {selectedCommission.processedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Processed
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedCommission.processedAt)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsRejectModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-red-600 px-6 py-4">
                <h3 className="text-xl font-semibold text-white">
                  Reject Commission Payouts
                </h3>
                <p className="text-sm text-red-100">
                  {selectedCommissions.length} payout(s) will be rejected
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rejection Reason *
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  This reason will be emailed to the dealer.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter the reason for rejection..."
                />
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsRejectModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || bulkReject.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkReject.isPending ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export History Modal */}
      {isExportHistoryModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsExportHistoryModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">
                  Export History
                </h3>
                <button
                  onClick={() => setIsExportHistoryModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-96 overflow-y-auto">
                {exportHistory.length > 0 ? (
                  <div className="space-y-3">
                    {exportHistory.map((item) => (
                      <div
                        key={item.batchId}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-mono text-gray-900 dark:text-white">
                            {item.batchId}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(item.exportedAt)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {item.count} payouts
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCurrency(item.totalAmount)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400">
                    No exports yet
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Commissions;
